/**
 * The Slab Frame (Design Bible 4), in its public form (Design Bible 19.2).
 *
 * The structure is the Bible's, unchanged: a label bar carrying the same three
 * facts in the same three positions, a 16px chamfer at the top-left, a 1px
 * inner bezel inset 6px, and an identifier line beneath. It belongs to a
 * `PhysicalItem` and to nothing else (4.3's prohibition).
 *
 * One rule is amended for this surface, and the amendment is recorded in the
 * Design Bible rather than left as drift. 4.3 selects the `encapsulated`
 * variant from `(grading_company, cert_number)`. Core's WP04 public projection
 * publishes `grading_company` and `grade` but *not* `cert_number`, so applying
 * 4.3 literally here would render the `raw` variant for a slab that is
 * demonstrably graded — and the `raw` variant structurally cannot show a grade,
 * so an approved, published grade would disappear from the customer surface
 * that exists to show it.
 *
 * The public selector is therefore `(grading_company, grade)`, and the cert
 * slot is never empty:
 *
 * - When the contract carries a certification number it is rendered grouped in
 *   fours, selectable, un-truncated — the cross-check 1.2 is built around.
 * - When it does not, the slot carries an explicit sentence saying the number
 *   is not published here and how to get it. 4.3's actual concern is a
 *   *placeholder* that implies a fact nobody verified; a stated absence is the
 *   opposite of that, and it keeps the promise in 1.2 that this surface never
 *   looks like it is hiding anything.
 *
 * The `raw` variant is unchanged in spirit and still has no grade slot in the
 * markup at all: it renders when core published no grading company, which is
 * exactly the sealed/loose/accessory case 4.3 was written for.
 */

import type { ReactNode } from "react";
import { Identifier } from "./primitives.js";

export interface SlabFrameProps {
  readonly sku: string;
  readonly gradingCompany?: string | undefined;
  readonly grade?: string | undefined;
  readonly certNumber?: string | undefined;
  /** Public stand-in for `item_type`, which the public contract does not carry. */
  readonly platform: string;
  readonly editionVariant?: string | undefined;
  readonly size?: "sm" | "md" | "lg";
  /** 4.4: fires once per item view. Never on hover, never on scroll, never in a list. */
  readonly seat?: boolean;
  readonly children?: ReactNode;
}

export function SlabFrame(props: SlabFrameProps): ReactNode {
  const encapsulated =
    props.gradingCompany !== undefined &&
    props.gradingCompany.length > 0 &&
    props.grade !== undefined &&
    props.grade.length > 0;

  const size = props.size ?? "md";
  const className = [
    "slab",
    `slab--${size}`,
    encapsulated ? "" : "slab--raw",
    props.seat === true ? "slab--seat" : ""
  ]
    .filter((token) => token.length > 0)
    .join(" ");

  return (
    <article className={className} data-variant={encapsulated ? "encapsulated" : "raw"}>
      {/*
        * A div, not a `header`. The label bar is a caption inside the frame,
        * and HTML's scoping exception for `header` inside `article` is not
        * implemented consistently by accessibility tooling — a page listing 24
        * slabs would otherwise expose 24 banner landmarks.
        */}
      <div className="slab-label">
        {encapsulated ? (
          <>
            <span className="t-label slab-label-grader">{props.gradingCompany}</span>
            <span className="slab-label-cert">
              {props.certNumber === undefined ? (
                <span className="t-body-s slab-cert-absent">Certification number on request</span>
              ) : (
                <Identifier label="Certification number" size="m" value={props.certNumber} />
              )}
            </span>
            <span className="t-id t-id--m slab-label-grade">{props.grade}</span>
          </>
        ) : (
          <>
            <span className="t-label slab-label-grader">{props.platform}</span>
            <span className="slab-label-cert">
              <Identifier label="SKU" size="m" value={props.sku} />
            </span>
          </>
        )}
      </div>
      {props.children === undefined ? null : <div className="slab-well">{props.children}</div>}
      <div className="slab-identifiers">
        <span>
          <span className="t-label">SKU</span> <Identifier label="SKU" value={props.sku} />
        </span>
        {props.editionVariant === undefined || props.editionVariant.length === 0 ? null : (
          <span>
            <span className="t-label">Variant</span>{" "}
            <span className="t-body-s">{props.editionVariant}</span>
          </span>
        )}
      </div>
    </article>
  );
}

/** 13.1: the skeleton keeps the chamfer and the label bar, so nothing shifts. */
export function SlabFrameSkeleton({ size = "md" }: { size?: "sm" | "md" | "lg" }): ReactNode {
  return (
    <div aria-hidden="true" className={`slab slab--${size} slab--skeleton`}>
      <div className="slab-label">
        <span className="skeleton skeleton--label" />
      </div>
      <div className="slab-well">
        <span className="skeleton skeleton--media" />
      </div>
      <div className="slab-identifiers">
        <span className="skeleton skeleton--line" />
      </div>
    </div>
  );
}
